import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", booking: null });

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", booking: data.booking });
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "failed", booking: null });
    };
    if (sessionId) poll();
    else setState({ phase: "failed", booking: null });
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-6 py-32">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-lg w-full border border-line/70 bg-sand p-10 md:p-14 text-center"
      >
        {state.phase === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="w-12 h-12 text-terra animate-spin mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl tracking-tight">{t("payment.polling")}</h1>
          </div>
        )}
        {state.phase === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 className="w-12 h-12 text-terra mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">{t("payment.successTitle")}</h1>
            <p className="text-ink/60 text-sm leading-relaxed mb-8">{t("payment.successSub")}</p>
            {state.booking && (
              <div className="border border-line/70 divide-y divide-line/60 text-sm text-left mb-8" data-testid="booking-confirmation-details">
                <div className="flex justify-between px-5 py-3.5">
                  <span className="text-ink/50">{t("payment.bookingRef")}</span>
                  <span className="font-semibold tracking-widest" data-testid="booking-ref-value">{state.booking.booking_id}</span>
                </div>
                <div className="flex justify-between px-5 py-3.5">
                  <span className="text-ink/50">{t("payment.dates")}</span>
                  <span>{state.booking.check_in} → {state.booking.check_out}</span>
                </div>
                <div className="flex justify-between px-5 py-3.5">
                  <span className="text-ink/50">{t("payment.paid")}</span>
                  <span className="font-serif text-lg text-terra">€{state.booking.total.toFixed(2)}</span>
                </div>
              </div>
            )}
            <Link to="/" data-testid="success-back-home" className="inline-flex px-8 py-4 bg-terra text-sand text-xs font-bold tracking-[0.2em] uppercase hover:bg-clay transition-colors">
              {t("payment.backHome")}
            </Link>
          </div>
        )}
        {state.phase === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="w-12 h-12 text-clay mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl tracking-tight mb-4">{t("payment.cancelTitle")}</h1>
            <p className="text-ink/60 text-sm mb-8">{t("payment.failed")}</p>
            <Link to="/" data-testid="failed-back-home" className="inline-flex px-8 py-4 border border-ink/20 text-xs font-bold tracking-[0.2em] uppercase hover:bg-ink hover:text-sand transition-colors">
              {t("payment.backHome")}
            </Link>
          </div>
        )}
      </motion.div>
    </main>
  );
}
